import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import styles from './style.module.scss'

const formatEuro = value => {
	const number = Number(value)
	if (value === null || value === undefined || value === '' || Number.isNaN(number)) return value || '—'

	return number.toLocaleString('de-AT', { style: 'currency', currency: 'EUR' })
}

/**
 * Markt- und Risikoanalyse eines Unternehmens, nur zum Lesen.
 *
 * Steht als eigener Baustein da, weil sie an zwei Stellen gebraucht wird: im
 * Firmenprofil der Verwaltung und im Unternehmensprofil der Lehrkraft. Beide
 * zeigen sie ueber `views/common/CompanyProfile/components/RiskTab` im
 * Abschnitt „Risikoanalyse". Waeren es zwei Bausteine, liefen sie beim
 * naechsten Feld auseinander.
 */
export default function RiskAnalysisDetails({ riskAnalysis }) {
	const { t } = useTranslation()

	if (!riskAnalysis) return <div className={styles.empty}>{t('No risk analysis yet')}</div>

	const { targetGroup, productUsage, customer, salesForecast, riskStrategy, totalAmoutOfSales } = riskAnalysis

	return (
		<div className={styles.root}>
			<section className={styles.block}>
				<h3 className={styles.blockTitle}>{t('Market Description')}</h3>
				<div className={styles.textFields}>
					<div className={styles.field}>
						<h4 className={styles.label}>{t('Target group(s)')}</h4>
						<p className={styles.text}>{targetGroup || <span className={styles.emptyValue}>—</span>}</p>
					</div>
					<div className={styles.field}>
						<h4 className={styles.label}>{t('Product usage')}</h4>
						<p className={styles.text}>{productUsage || <span className={styles.emptyValue}>—</span>}</p>
					</div>
					<div className={styles.field}>
						<h4 className={styles.label}>{t('Customer contact, sales strategy')}</h4>
						<p className={styles.text}>{customer || <span className={styles.emptyValue}>—</span>}</p>
					</div>
				</div>
			</section>

			<section className={styles.block}>
				<h3 className={styles.blockTitle}>{t('Sales forecast')}</h3>
				{salesForecast?.length ? (
					<div className={styles.tableScroll}>
						<table className={styles.table}>
							<colgroup>
								<col />
								<col className={styles.colNumeric} />
								<col className={styles.colNumeric} />
								<col className={styles.colNumeric} />
							</colgroup>
							<thead>
								<tr>
									<th>{t('Product / Service')}</th>
									<th className={styles.numeric}>{t('Price (€) / unit')}</th>
									<th className={styles.numeric}>{t('Expected sales (number)')}</th>
									<th className={styles.numeric}>{t('Expected turnover (€)')}</th>
								</tr>
							</thead>
							<tbody>
								{salesForecast.map((item, index) => (
									<tr key={index}>
										<td>{item?.product}</td>
										<td className={styles.numeric}>{formatEuro(item?.price)}</td>
										<td className={clsx(styles.numeric, styles.count)}>{item?.expectedSales}</td>
										<td className={styles.numeric}>{formatEuro(item?.expectedTurnover)}</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr>
									<td colSpan={3}>{t('Estimated annual sales, total')}</td>
									<td className={styles.numeric}>{formatEuro(totalAmoutOfSales)}</td>
								</tr>
							</tfoot>
						</table>
					</div>
				) : (
					<div className={styles.empty}>{t('No sales forecast yet')}</div>
				)}
			</section>

			<section className={styles.block}>
				<h3 className={styles.blockTitle}>{t('Risk and strategy')}</h3>
				{riskStrategy?.length ? (
					<div className={styles.riskGrid}>
						{riskStrategy.map((item, index) => (
							<div key={index} className={styles.riskCard}>
								<div className={styles.field}>
									<h4 className={styles.label}>{t('Risk')}</h4>
									<p className={styles.text}>
										{item?.risk || <span className={styles.emptyValue}>—</span>}
									</p>
								</div>
								<div className={styles.field}>
									<h4 className={styles.label}>{t('Avoidance strategy')}</h4>
									<p className={styles.text}>
										{item?.strategy || <span className={styles.emptyValue}>—</span>}
									</p>
								</div>
							</div>
						))}
					</div>
				) : (
					<div className={styles.empty}>{t('No risks recorded yet')}</div>
				)}
			</section>
		</div>
	)
}

RiskAnalysisDetails.propTypes = {
	riskAnalysis: PropTypes.object
}
